import React, { useState } from 'react'
import {
    Grid,
    TextField,
    MenuItem,
    FormControl,
    InputLabel,
    Select,
    InputAdornment,
    IconButton,
    Tooltip,
    CircularProgress,
} from '@mui/material';
import SectionTitle from '../../../ui/sectionTitle';
import { Search } from '@mui/icons-material';
import { SelectLocation } from '../../../common';
import { CatalogService } from '../../../../service/catalogService';
import { toast } from 'react-toastify';

function ValidFrameBasicInfo({formik, setSupplierDialogOpen, setBrandDialogOpen, labels, disableModelNumber = false}) {
    const [searchingCatalog, setSearchingCatalog] = useState(false);
    
    const updateLocation = (location) => {
        formik.setFieldValue('location', location)
    }

    const handleCatalogSearch = async () => {
        const modelNumber = formik.values.modelNumber?.trim();
        if (!modelNumber) {
            toast.warning('Please enter a model number to search');
            return;
        }

        setSearchingCatalog(true);
        try {
            const response = await CatalogService.findFramesByModel(modelNumber);
            
            if (response.code === 0 && response.data?.data?.length > 0) {
                const catalogProduct = response.data.data[0];
                const frame = catalogProduct.frame;
                const brand = catalogProduct.brand;

                // Store catalog product ID for linking
                formik.setFieldValue('catalogProductId', catalogProduct.id);

                // Populate model number with full catalog model number
                if (frame?.modelNumber) {
                    formik.setFieldValue('modelNumber', frame.modelNumber);
                }

                // Populate brand name (backend will match/create tenant brand)
                if (brand?.name) {
                    formik.setFieldValue('brand', brand.name);
                }
                
                // Populate UPC/SKU
                if (frame?.upcOrEan) {
                    formik.setFieldValue('supplierUpc', frame.upcOrEan);
                    formik.setFieldValue('sku', frame.upcOrEan);
                }
                
                // Populate properties (will be merged from catalog on backend)
                if (frame) {
                    const properties = {};
                    if (frame.gender) properties.gender = frame.gender;
                    if (frame.a) {
                        properties.aMeasurement = parseFloat(frame.a);
                    }
                    if (frame.b) properties.bMeasurement = parseFloat(frame.b);
                    if (frame.dbl) {
                        properties.dblMeasurement = parseFloat(frame.dbl);
                    }
                    if (frame.temple) properties.templeLength = parseFloat(frame.temple);
                    if (frame.ed) properties.edMeasurement = parseFloat(frame.ed);
                    if (frame.frameType) properties.frameType = frame.frameType;
                    if (frame.shape?.name) properties.shape = frame.shape.name;
                    if (frame.material?.name) properties.material = frame.material.name;
                    if (frame.colorName) properties.color = frame.colorName;
                    
                    formik.setFieldValue('properties', {
                        ...formik.values.properties,
                        ...properties
                    });
                }

                toast.success(`Found in catalog: ${brand?.name || ''} ${frame?.modelNumber || ''}`);
            } else {
                toast.info('Not found in catalog. You can enter details manually.');
            }
        } catch (error) {
            console.error('Error searching catalog:', error);
            toast.error('Error searching catalog');
        } finally {
            setSearchingCatalog(false);
        }
    };

    return (
        <>
            <Grid item xs={12}>
                <SectionTitle title="Basic Information" />
            </Grid>
            <Grid item xs={12} sm={4} mt={1}>
                <TextField
                    id={"modelNumber"}
                    name="modelNumber"
                    label={labels.modelNumber ||"Model Number"}
                    size='small'
                    fullWidth
                    disabled={disableModelNumber}
                    value={formik.values.modelNumber}
                    onChange={formik.handleChange}
                    onKeyPress={(e) => {
                        if (e.key === 'Enter') {
                            e.preventDefault();
                            handleCatalogSearch();
                        }
                    }}
                    error={Boolean(formik.errors.modelNumber)}
                    helperText={formik.errors.modelNumber}
                    InputProps={{
                        endAdornment: !disableModelNumber ? (
                            <InputAdornment position="end">
                                <Tooltip title="Search catalog">
                                    <IconButton
                                        onClick={handleCatalogSearch}
                                        disabled={searchingCatalog}
                                        size="small"
                                    >
                                        {searchingCatalog ? (
                                            <CircularProgress size={20} />
                                        ) : (
                                            <Search />
                                        )}
                                    </IconButton>
                                </Tooltip>
                            </InputAdornment>
                        ) : null,
                    }}
                />
            </Grid>
            {/* NOT NEEDED FOR NOW <Grid item xs={12} sm={4} mt={1}>
                <TextField
                    id="supplierName"
                    name="supplierName"
                    label="Supplier Name"
                    size='small'
                    fullWidth
                    value={formik.values.supplierName}
                    onChange={formik.handleChange}
                    error={Boolean(formik.errors.supplierName)}
                    helperText={formik.errors.supplierName}
                    inputProps={{
                        style: {
                            textTransform: 'capitalize'
                        }
                    }}
                    InputProps={{
                        endAdornment: (
                            <InputAdornment position="end">
                                <IconButton
                                    onClick={() => setSupplierDialogOpen(true)}
                                >
                                    <Search />
                                </IconButton>
                            </InputAdornment>
                        ),
                    }}
                />
            </Grid> */}
            <Grid item xs={12} sm={4} mt={1}>
                <TextField
                    id="brand"
                    name="brand"
                    label="Brand"
                    size='small'
                    fullWidth
                    value={formik.values.brand}
                    onChange={formik.handleChange}
                    error={Boolean(formik.errors.brand)}
                    helperText={formik.errors.brand}
                    inputProps={{
                        style: {
                            textTransform: 'capitalize'
                        }
                    }}
                    // InputProps={{
                    //     endAdornment: (
                    //         <InputAdornment position="end">
                    //             <IconButton
                    //                 onClick={() => setBrandDialogOpen(true)}
                    //             >
                    //                 <Search />
                    //             </IconButton>
                    //         </InputAdornment>
                    //     ),
                    // }}
                />
            </Grid>
            <Grid item xs={12} sm={3} mt={1}>
                <TextField
                    id="sku"
                    name="sku"
                    label="SKU"
                    size='small'
                    fullWidth
                    onBlur={formik.handleBlur}
                    value={formik.values.sku}
                    onChange={formik.handleChange}
                    error={formik.touched.sku && Boolean(formik.errors.sku)}
                    helperText={formik.touched.sku && formik.errors.sku}
                />
            </Grid>            
            <Grid item xs={12} sm={2} mt={1}>
                <TextField
                    id="internalUpc"
                    name="internalUpc"
                    label="Internal UPC"
                    size='small'
                    fullWidth
                    onBlur={formik.handleBlur}
                    value={formik.values.internalUpc}
                    onChange={formik.handleChange}
                    error={formik.touched.internalUpc && Boolean(formik.errors.internalUpc)}
                    helperText={formik.touched.internalUpc && formik.errors.internalUpc}
                />
            </Grid>
            <Grid item xs={12} sm={2} mt={1}>
                <TextField
                    id="supplierUpc"
                    name="supplierUpc"
                    label="Supplier UPC"
                    size='small'
                    fullWidth
                    onBlur={formik.handleBlur}
                    value={formik.values.supplierUpc}
                    onChange={formik.handleChange}
                    error={formik.touched.supplierUpc && Boolean(formik.errors.supplierUpc)}
                    helperText={formik.touched.supplierUpc && formik.errors.supplierUpc}
                />
                {/* <FormControl fullWidth>
                                <InputLabel>Type</InputLabel>
                                <Select
                                    id="type"
                                    name="type"
                                    label="Type"
                                    size='small'
                                    fullWidth
                                    value={formik.values.type}
                                    onChange={formik.handleChange}
                                    error={formik.touched.type && Boolean(formik.errors.type)}
                                >
                                    {frameTypes.map((type) => (
                                        <MenuItem key={type} value={type}>
                                            {type}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl> */}
            </Grid>
            <Grid item xs={12} sm={2} mt={1} mr={2}>
                {
                    <SelectLocation fullWidth={true} location={formik.values.location.id || ''} error={Boolean(formik.errors.location)} required={true}
                    helperText={formik.errors.location} setLocation={updateLocation}
                 />
                }
                
            </Grid>
            <Grid item xs={12} sm={2} mt={1}>
                <FormControl fullWidth>
                    <InputLabel id='status-label'>Status</InputLabel>
                    <Select
                        id="status"
                        name="status"
                        label="Status"
                        size='small'
                        value={formik.values.status}
                        onChange={formik.handleChange}
                        error={formik.touched.status && Boolean(formik.errors.status)}
                    >
                        <MenuItem value="active">Active</MenuItem>
                        <MenuItem value="inactive">Inactive</MenuItem>
                    </Select>
                </FormControl>
            </Grid>
        </>
    )
}

export default ValidFrameBasicInfo